/**
 * Winner Computation Utility
 *
 * Shared logic for determining the winner of a comparison based on scoring results.
 * Used by ComparisonDetail and ComparisonsList for consistent winner determination.
 *
 * Updated to support both legacy ScoringResult[] format and new Response.scoreOverall/scores fields.
 */

import type { ModelResponse, ScoringResult } from '../services/comparison';
import type { Response } from '../lib/types/results';

export interface WinnerResult {
  winnerId: string | null;
  winnerScore: number | null;
}

/**
 * Computes the winner for a set of responses based on composite scores from LLM judge.
 *
 * Algorithm:
 * 1. If composite scores provided, use those (highest score wins)
 * 2. Otherwise, return null (no winner until scored)
 *
 * NO FALLBACKS: Only composite scores from LLM judge are used to determine winners.
 * This ensures CLV and CDV are synchronized and only show winners when scoring is complete.
 *
 * @param responses - Array of responses to evaluate (ModelResponse or Response type)
 * @param scoringResults - Legacy scoring results (DEPRECATED, kept for backward compatibility)
 * @param compositeScores - Composite scores from LLM judge (REQUIRED for winner determination)
 * @returns Object with winnerId (modelId of winner) and winnerScore (composite score)
 */
export function computeWinner(
  responses: ModelResponse[] | Response[],
  scoringResults: ScoringResult[] = [],
  compositeScores?: Array<{ modelId: string; compositeScore: number | null }>
): WinnerResult {
  // Filter to completed responses only
  const completedResponses = responses.filter(r => r.status === 'completed');

  if (completedResponses.length === 0) {
    return { winnerId: null, winnerScore: null };
  }

  // ONLY use composite scores if provided - no fallbacks
  if (compositeScores && compositeScores.length > 0) {
    const sorted = [...compositeScores].sort((a, b) => (b.compositeScore ?? -1) - (a.compositeScore ?? -1));

    // Only return winner if score is meaningful (> 0)
    // Score of null or 0 indicates no valid scoring data
    if (sorted[0].compositeScore == null || sorted[0].compositeScore <= 0) {
      return { winnerId: null, winnerScore: null };
    }

    return {
      winnerId: sorted[0].modelId,
      winnerScore: sorted[0].compositeScore
    };
  }

  // NO FALLBACKS - return null if no composite scores
  // This ensures winners only show after LLM judge scoring is complete
  return { winnerId: null, winnerScore: null };
}
